import { useCallback, useEffect, useState } from 'react';
import { consultantApi } from '../../../utils/consultantApi';

// «صف سوالات» — questions the consult agent couldn't answer from
// search_knowledge. Each row is answered (creates a new knowledge_note
// and closes the question) or dismissed with an optional reason.

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fa-IR', {
      dateStyle: 'short', timeStyle: 'short',
    });
  } catch { return iso; }
}

export default function PendingQuestionsPanel() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [openId, setOpenId]   = useState(null);    // id of the row whose modal is open
  const [modalMode, setModalMode] = useState(null); // 'answer' | 'dismiss'
  const [draft, setDraft]     = useState('');
  const [saving, setSaving]   = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await consultantApi.listPending();
      setItems(r.pending || []);
    } catch (e) {
      setError(e.message || 'خواندن صف ناموفق بود.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const openModal = (id, mode) => {
    setOpenId(id); setModalMode(mode); setDraft(''); setError(null);
  };
  const closeModal = () => { setOpenId(null); setModalMode(null); setDraft(''); };

  const submit = async () => {
    if (!openId) return;
    setSaving(true); setError(null);
    try {
      if (modalMode === 'answer') {
        if (!draft.trim()) throw new Error('متن پاسخ خالی است.');
        await consultantApi.answerPending(openId, draft);
      } else {
        await consultantApi.dismissPending(openId, draft || null);
      }
      closeModal();
      await refresh();
    } catch (e) {
      setError(e.message || 'عملیات ناموفق بود.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="consultant-panel pending-panel">
      <header className="pending-header">
        <div>
          <h2>صف سوالات</h2>
          <p className="pending-intro">
            سوال‌هایی که مشاور هوشمند نتوانست از پایگاه دانش پاسخ بدهد و
            از شما خواست بررسی کنید. با پاسخ‌دادن یک نتِ جدید در پایگاه
            دانش ساخته می‌شود؛ مشتری بعدی همان سوال را با پاسخِ جدید
            دریافت می‌کند.
          </p>
        </div>
        <button className="consultant-btn secondary" onClick={refresh} disabled={loading}>
          {loading ? '…' : 'تازه‌سازی'}
        </button>
      </header>

      {error && !openId && (
        <div className="pending-error" role="alert">{error}</div>
      )}

      {loading ? (
        <p className="consultant-empty">در حال بارگذاری…</p>
      ) : items.length === 0 ? (
        <p className="consultant-empty">
          همهٔ سوال‌ها پاسخ داده شده‌اند 🎉
        </p>
      ) : (
        <ul className="pending-list">
          {items.map((q) => (
            <li key={q.id} className="pending-row">
              <div className="pending-row-main">
                <div className="pending-question">{q.question_text}</div>
                {q.context && <div className="pending-context">{q.context}</div>}
                <div className="pending-meta">
                  <span>پرسیده شده در: {fmtTime(q.asked_at)}</span>
                  {q.consultation_id && (
                    <span className="pending-consult-id">گفتگو: {q.consultation_id.slice(0,8)}…</span>
                  )}
                </div>
              </div>
              <div className="pending-row-actions">
                <button className="consultant-btn" onClick={() => openModal(q.id, 'answer')}>پاسخ</button>
                <button className="consultant-btn secondary" onClick={() => openModal(q.id, 'dismiss')}>رد</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {openId && (
        <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="modal-box pending-modal" role="dialog">
            <h3>{modalMode === 'answer' ? 'پاسخ به سوال' : 'رد سوال'}</h3>
            <p className="pending-modal-q">
              {items.find((q) => q.id === openId)?.question_text}
            </p>
            <textarea
              className="consultant-input"
              style={{ width: '100%', minHeight: 110 }}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={modalMode === 'answer'
                ? 'پاسخِ کوتاه و دقیق بنویسید - مستقیماً به‌عنوان یک نتِ دانش ذخیره می‌شود.'
                : 'دلیلِ ردکردن (اختیاری).'}
              disabled={saving}
              autoFocus
            />
            {error && <div className="pending-error" role="alert">{error}</div>}
            <div className="pending-modal-actions">
              <button className="consultant-btn secondary" onClick={closeModal} disabled={saving}>انصراف</button>
              <button
                className="consultant-btn"
                onClick={submit}
                disabled={saving || (modalMode === 'answer' && !draft.trim())}
              >
                {saving
                  ? 'در حال ذخیره…'
                  : modalMode === 'answer' ? 'ذخیره و بستنِ سوال' : 'رد و بستنِ سوال'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
